import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildLeadFormPayload,
  mapCountryOptions,
  normalizeLeadFormFromRow,
  validateLeadForm,
} from './use-leads-list.js';

export function useLeadCreateForm() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => normalizeLeadFormFromRow(null));
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);

  useEffect(() => {
    let isCancelled = false;

    const loadCountries = async () => {
      try {
        const response = await adminApi.getCountryDictionary(token);
        if (!isCancelled) {
          setCountryOptions(mapCountryOptions(response));
        }
      } catch {
        if (!isCancelled) {
          setCountryOptions([]);
        }
      }
    };

    loadCountries();

    return () => {
      isCancelled = true;
    };
  }, [token]);

  const onLeadFormChange = useCallback((field, value) => {
    setForm((previous) => {
      const path = String(field || '').split('.');
      if (path[0] === 'details' && path.length === 3) {
        const index = Number.parseInt(path[1], 10);
        const key = path[2];
        if (!Number.isInteger(index) || index < 0 || !['name', 'value'].includes(key)) {
          return previous;
        }

        const details = [...previous.details];
        details[index] = {
          name: '',
          value: '',
          ...(details[index] || {}),
          [key]: value,
        };
        return {
          ...previous,
          details,
        };
      }

      return {
        ...previous,
        [field]: value,
      };
    });
    setFormErrors({});
    setError('');
  }, []);

  const addLeadDetailRow = useCallback(() => {
    setForm((previous) => ({
      ...previous,
      details: [...previous.details, { name: '', value: '' }],
    }));
  }, []);

  const removeLeadDetailRow = useCallback((index) => {
    setForm((previous) => ({
      ...previous,
      details: previous.details.filter((_, itemIndex) => itemIndex !== index),
    }));
  }, []);

  const cancelLeadForm = useCallback(() => {
    if (!isSaving) {
      navigate('/leads');
    }
  }, [isSaving, navigate]);

  const saveLeadForm = useCallback(async (event) => {
    event?.preventDefault?.();
    const errors = validateLeadForm(form, true, false);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      await adminApi.createLead(token, buildLeadFormPayload(form, true));
      navigate('/leads');
    } catch (requestError) {
      setError(requestError.message || 'Failed to create lead');
    } finally {
      setIsSaving(false);
    }
  }, [form, navigate, token]);

  return {
    form,
    formErrors,
    error,
    isSaving,
    countryOptions,
    onLeadFormChange,
    addLeadDetailRow,
    removeLeadDetailRow,
    saveLeadForm,
    cancelLeadForm,
  };
}
